import React, { forwardRef } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';

// Registrar o locale pt-BR para o react-datepicker
registerLocale('pt-BR', ptBR);

/**
 * @interface DatePickerProps
 * @description Define as propriedades para o componente DatePicker.
 * @property {Date | null} selected - A data atualmente selecionada no DatePicker.
 * @property {(date: Date | null) => void} onChange - Função de callback chamada quando a data é alterada.
 * @property {string} [dateFormat='dd/MM/yyyy'] - O formato de exibição da data.
 * @property {Date | null} [minDate=null] - A data mínima permitida para seleção.
 * @property {Date | null} [maxDate=null] - A data máxima permitida para seleção.
 * @property {string} [className=''] - Classes CSS adicionais para o contêiner do input.
 * @property {string} [placeholderText='Selecione uma data'] - Texto de placeholder para o campo de input.
 * @property {boolean} [disabled=false] - Se o DatePicker deve estar desabilitado.
 * @property {boolean} [readOnly=false] - Se o campo de input deve ser somente leitura.
 * @property {boolean} [showTimeSelect=false] - Se a seleção de hora deve ser exibida.
 * @property {string} [timeFormat='HH:mm'] - O formato de exibição da hora, se showTimeSelect for true.
 * @property {number} [timeIntervals=30] - O intervalo de minutos para a seleção de hora.
 * @property {boolean} [showMonthDropdown=false] - Se um dropdown para seleção de mês deve ser exibido.
 * @property {boolean} [showYearDropdown=false] - Se um dropdown para seleção de ano deve ser exibido.
 * @property {'scroll' | 'select'} [dropdownMode='scroll'] - O modo de exibição dos dropdowns de mês/ano.
 */
interface DatePickerProps {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  dateFormat?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  className?: string;
  placeholderText?: string;
  disabled?: boolean;
  readOnly?: boolean;
  showTimeSelect?: boolean;
  timeFormat?: string;
  timeIntervals?: number;
  showMonthDropdown?: boolean;
  showYearDropdown?: boolean;
  dropdownMode?: 'scroll' | 'select';
}

/**
 * @component CustomDatePickerInput
 * @description Um componente de input personalizado para o ReactDatePicker, estilizado com Tailwind CSS.
 * @param {React.InputHTMLAttributes<HTMLInputElement>} props - Propriedades padrão de um input HTML.
 * @param {React.Ref<HTMLInputElement>} ref - Ref para o elemento input.
 * @returns {JSX.Element} Um elemento input estilizado.
 */
const CustomDatePickerInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ value, onClick, className, placeholder, disabled, readOnly }, ref) => (
    <input
      type="text"
      className={`
        w-full p-3 rounded-lg border border-primary/20
        bg-background text-text placeholder-secondary/70
        focus:outline-none focus:ring-2 focus:ring-primary
        transition-all duration-200 ease-in-out
        ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
        ${className || ''}
      `}
      onClick={onClick}
      value={value}
      ref={ref}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
    />
  )
);

/**
 * @component DatePicker
 * @description Um componente de seleção de data e hora moderno e estilizado, utilizando `react-datepicker`
 * e Tailwind CSS, aderindo ao Design System do TugasCoins.
 *
 * Para que o estilo do calendário popup seja aplicado corretamente, é necessário que o CSS base do
 * `react-datepicker` (`react-datepicker/dist/react-datepicker.css`) seja importado globalmente
 * (por exemplo, em `TugasCoins/src/styles/globals.css`) e que as classes de override do Tailwind
 * para o `react-datepicker` também estejam definidas em `globals.css` conforme o Design System.
 *
 * @param {DatePickerProps} props - As propriedades para configurar o DatePicker.
 * @returns {JSX.Element} O componente DatePicker renderizado.
 */
const DatePicker: React.FC<DatePickerProps> = ({
  selected,
  onChange,
  dateFormat = 'dd/MM/yyyy',
  minDate = null,
  maxDate = null,
  className = '',
  placeholderText = 'Selecione uma data',
  disabled = false,
  readOnly = false,
  showTimeSelect = false,
  timeFormat = 'HH:mm',
  timeIntervals = 30,
  showMonthDropdown = false,
  showYearDropdown = false,
  dropdownMode = 'scroll',
}) => {
  return (
    <div className={`TugasCoins-datepicker-wrapper ${className}`}>
      <ReactDatePicker
        selected={selected}
        onChange={onChange}
        dateFormat={showTimeSelect ? `${dateFormat} ${timeFormat}` : dateFormat}
        locale="pt-BR"
        minDate={minDate}
        maxDate={maxDate}
        placeholderText={placeholderText}
        disabled={disabled}
        readOnly={readOnly}
        showTimeSelect={showTimeSelect}
        timeFormat={timeFormat}
        timeIntervals={timeIntervals}
        showMonthDropdown={showMonthDropdown}
        showYearDropdown={showYearDropdown}
        dropdownMode={dropdownMode}
        customInput={<CustomDatePickerInput />}
        // Classes para estilizar o calendário popup com Tailwind (requer overrides em globals.css)
        calendarClassName="TugasCoins-datepicker-calendar"
        dayClassName={() => "TugasCoins-datepicker-day"}
        popperClassName="TugasCoins-datepicker-popper"
        wrapperClassName="w-full"
      />
    </div>
  );
};

export default DatePicker;